<template>
    <FormLayout>
        <FormLayoutRow>
            <FormLayoutLabel text="eventinfobase.name" />
            <InputText
                ref="nameInput"
                :autofocus="true"
                v-model="model.Name"
                :maxlength="100"
                :validator="new ValidatorDef({ Min: 0, Max: 0, Required: true })"
            />
        </FormLayoutRow>
        <FormLayoutRow v-if="showOrganizer">
            <FormLayoutLabel text="eventinfobase.organizerid" />
            <InputOrganizerSelect v-model="model.OrganizerID" />
        </FormLayoutRow>

        <FormLayoutRow v-if="showProfile">
            <FormLayoutLabel text="eventinfobase.profileid" />
            <InputProfileSelect
                v-model="model.ProfileID"
                :row="model"
                @loadedprofilecount="onLoadedProfileCount"
            />
        </FormLayoutRow>
        <FormLayoutRow>
            <FormLayoutLabel text="eventinfobase.date" />
            <InputDateTime
                format="DATE"
                v-model="model.Date"
            />
        </FormLayoutRow>
        <FormLayoutRow>
            <FormLayoutLabel text="eventinfobase.location" />
            <div class="d-flex flex-row flex-nowrap flex-fill">
                <InputText v-model="LocationName" />

                <ButtonComp
                    button-type="Secondary"
                    icon="fa-map-pin"
                    @click="onAddLocation"
                >
                    {{ $tm('eventinfo.addlocation') }}
                </ButtonComp>
            </div>
        </FormLayoutRow>

        <DisplaySeparator v-model="separator" />

        <FormLayoutRow>
            <FormLayoutLabel text="eventinfobase.federationid" />
            <InputSelect
                :datasource="FederationsSourceFilter"
                v-model="model.FederationID"
                :row="model"
            />
        </FormLayoutRow>
        <FormLayoutRow>
            <FormLayoutLabel text="eventinfobase.sportid" />
            <InputSelect
                :datasource="SportsSourceFilter"
                v-model="model.SportID"
                :row="model"
            />
        </FormLayoutRow>
        <FormLayoutRow>
            <FormLayoutLabel text="eventinfobase.sportlevelid" />
            <InputSelect
                :datasource="SportLevelsSourceFilter"
                v-model="model.SportlevelID"
                :row="model"
            />
        </FormLayoutRow>

        <FormLayoutRow>
            <FormLayoutLabel text="eventinfobase.disciplineid" />
            <InputSelect
                :datasource="DisciplinesSourceFilter"
                v-model="model.DisciplineID"
                :row="model"
            />
        </FormLayoutRow>
    </FormLayout>
</template>
<script setup lang="ts">
import { FormLayout, FormLayoutLabel, FormLayoutRow } from '@/components/Form';
import { EventinfoBase, EventLocation } from '@/objects/generated';
import { computed, inject, ref, watch, type Ref } from 'vue';
import { useCommonStoreRef } from '@/stores';
import type { IAlertMessage, SelectLine, SelectLineValueType } from '@/objects';
import { FieldAlertMessage } from '@/objects';
import {
    ButtonComp,
    DisplaySeparator,
    InputDateTime,
    InputOrganizerSelect,
    InputProfileSelect,
    InputSelect,
    InputText,
} from '@/components';
import { ValidatorDef, type IEditorComponent } from '@/objects/gui';
import { $tm } from '@/locize';
import EventinfoAddLocationView from '../EventDashboard/EventinfoAddLocationView.vue';
import { useDialogBuilder } from '@/composeables/dialog/dialogbuilder';

const model = defineModel<EventinfoBase>({ required: true });

const LocationName = computed({
    get: () => model.value.Location!.Name,
    set: (value: string) => {
        model.value.Location!.Name = value;
    },
});

const props = withDefaults(
    defineProps<{
        /**
         * Contains a list of events we can select as a source for contestants
         */
        eventslist?: EventinfoBase[] | null;
    }>(),
    {
        eventslist: null,
    },
);

const emits = defineEmits<{
    (e: 'loadedprofilecount', value: number): void;
}>();

const nameInput = ref<IEditorComponent>();

/**
 * The common store
 */
const comStore = useCommonStoreRef();

// Helper property for the eventType separator
const separator = ref<string>('event.eventtype');

// How many profiles are loaded for the current event
const loadedprofilecount = ref<number | null>(null);

/**
 * If we should show the organizer select box.
 */
const showOrganizer = computed(() => {
    return loadedprofilecount.value == null || comStore.Organizers.value.length > 1;
});

/**
 * If we should show the profile select box.
 */
const showProfile = computed(() => {
    return loadedprofilecount.value == null || loadedprofilecount.value != 1;
});

/**
 * Callback when the profile count is loaded.
 * @param count
 */
const onLoadedProfileCount = async (count: number) => {
    loadedprofilecount.value = count;
    emits('loadedprofilecount', count);
};

const FederationsSource = comStore.FederationsAsSelectLine;
const FederationsSourceFilter = (obj: EventinfoBase) => {
    const valuesToInclude = comStore.Federations.value
        .filter((x) => comStore.Organizers.value.find((z) => z.ID == obj.OrganizerID)?.CountryID == x.CountryID)
        .map((x) => x.ID as SelectLineValueType);

    // update if field does not have the correct value
    if (!valuesToInclude.includes(obj.FederationID)) {
        if (valuesToInclude.length > 0) {
            obj.FederationID = valuesToInclude[0] as number;
        } else {
            console.warn('obj.FederationID not set as we have no data in source');
        }
    }

    const ret = new Array<SelectLine>();
    for (const x of valuesToInclude) {
        const line = FederationsSource.value.get(x);
        if (line) {
            ret.push(line);
        }
    }

    return ret;
};

const SportsSource = comStore.SportsAsSelectLine;
const SportsSourceFilter = (obj: EventinfoBase) => {
    const valuesToInclude = comStore.Sports.value.filter((x) => x.FederationID == obj.FederationID).map((x) => x.ID as SelectLineValueType);

    // update if field does not have the correct value
    if (!valuesToInclude.includes(obj.SportID)) {
        if (valuesToInclude.length > 0) {
            obj.SportID = valuesToInclude[0] as number;
        } else {
            console.warn('obj.SportID not set as we have no data in source');
        }
    }

    const ret = new Array<SelectLine>();
    for (const x of valuesToInclude) {
        const line = SportsSource.value.get(x);
        if (line) {
            ret.push(line);
        }
    }
    return ret;
};

const SportLevelsSource = comStore.SportLevelsAsSelectLine;
const SportLevelsSourceFilter = (obj: EventinfoBase) => {
    const valuesToInclude = comStore.SportLevels.value.filter((x) => x.SportID == obj.SportID).map((x) => x.ID as SelectLineValueType);

    // update if field does not have the correct value
    if (!valuesToInclude.includes(obj.SportlevelID)) {
        if (valuesToInclude.length > 0) {
            obj.SportlevelID = valuesToInclude[0] as number | null;
        } else {
            console.warn('obj.SportlevelID not set as we have no data in source');
        }
    }

    const ret = new Array<SelectLine>();
    for (const x of valuesToInclude) {
        const line = SportLevelsSource.value.get(x);
        if (line) {
            ret.push(line);
        }
    }
    return ret;
};

const DisciplinesSource = comStore.DisciplinesAsSelectLine;
const DisciplinesSourceFilter = (obj: EventinfoBase) => {
    const valuesToInclude = comStore.Disciplines.value.filter((x) => x.SportID == obj.SportID).map((x) => x.ID as SelectLineValueType);

    // update if field does not have the correct value
    if (!valuesToInclude.includes(obj.DisciplineID)) {
        if (valuesToInclude.length > 0) {
            obj.DisciplineID = valuesToInclude[0] as number | null;
        } else {
            console.warn('obj.DisciplineID not set as we have no data in source');
        }
    }

    const ret = new Array<SelectLine>();
    for (const x of valuesToInclude) {
        const line = DisciplinesSource.value.get(x);
        if (line) {
            ret.push(line);
        }
    }
    return ret;
};

watch(
    () => props.eventslist,
    () => {
        if (props.eventslist != null && props.eventslist.length > 0) {
            const previousEvent = props.eventslist[0];

            // We default to the first event in the list if we dont have a name
            if ((model.value?.Name ?? '').trim() == '') {
                model.value.Name = `${previousEvent.Name} - ${$tm('eventinfo.copy')}`;
                model.value.Date = new Date();
            }

            // We map the Orgnaizationid, profileId, federationId, sportId and SportlevelId to the previous event
            model.value.OrganizerID = previousEvent.OrganizerID;
            model.value.ProfileID = previousEvent.ProfileID;
            model.value.FederationID = previousEvent.FederationID;
            model.value.SportID = previousEvent.SportID;
            model.value.SportlevelID = previousEvent.SportlevelID;
            model.value.DisciplineID = previousEvent.DisciplineID;
            model.value.Location = previousEvent.Location;
        }
    },
    { immediate: true },
);

const quickCreateAlertMessage = inject<Ref<Map<string, FieldAlertMessage>>>('QuickCreateAlertMessage') ?? new Map<string, IAlertMessage>();

watch(
    () => nameInput.value?.validateMessage,
    (nv, ov) => {
        if (quickCreateAlertMessage != null) {
            if (nv == null) {
                // @ts-ignore - Issue with unwrapping
                quickCreateAlertMessage.value.delete('eventinfobase.name');
            } else {
                // @ts-ignore - Issue with unwrapping
                quickCreateAlertMessage.value.set('eventinfobase.name', nv);
            }
        }
    },
    { immediate: true },
);

/**
 * We save the location when the event is updated in the Modal
 */
const onAddLocation = () => {
    const internalLocationModel = ref<EventLocation>(model.value.Location ?? EventLocation.FromJsValues({}));

    // Emit an event to the parent to open the location dialog
    // This is a placeholder, implement the actual logic to add a location
    useDialogBuilder()
        .withTitle('eventinfo.updatelocation')
        .withBodyComponent(EventinfoAddLocationView, {
            vmodel: internalLocationModel,
        })
        .withCloseButton({ action: () => {} })
        .withOkButton({
            action: () => {
                if (model.value.Location == null) {
                    model.value.Location = EventLocation.FromJsValues({});
                }

                model.value.Location.CountryISO3 = internalLocationModel.value.CountryISO3;
                model.value.Location.Latitude = internalLocationModel.value.Latitude;
                model.value.Location.Longitude = internalLocationModel.value.Longitude;
                model.value.Location.Name = internalLocationModel.value.Name;
            },
        })
        .withOptions({
            Size: 'md',
        })
        .Open();
};
</script>
